<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  color: {
    type: String,
    default: '#4A90E2',
  },
  size: {
    type: Number,
    default: 1,
  },
  items: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['click']);

const open = ref(false);
const paperOffsets = ref([
  { x: 0, y: 0 },
  { x: 0, y: 0 },
  { x: 0, y: 0 },
]);

const darkenColor = (hex, percent) => {
  let color = hex.startsWith('#') ? hex.slice(1) : hex;
  if (color.length === 3) {
    color = color
      .split('')
      .map(c => c + c)
      .join('');
  }
  const num = parseInt(color, 16);
  // eslint-disable-next-line no-bitwise
  let r = (num >> 16) & 0xff;
  // eslint-disable-next-line no-bitwise
  let g = (num >> 8) & 0xff;
  // eslint-disable-next-line no-bitwise
  let b = num & 0xff;
  r = Math.max(0, Math.min(255, Math.floor(r * (1 - percent))));
  g = Math.max(0, Math.min(255, Math.floor(g * (1 - percent))));
  b = Math.max(0, Math.min(255, Math.floor(b * (1 - percent))));

  // eslint-disable-next-line no-bitwise
  const combined = (1 << 24) + (r << 16) + (g << 8) + b;
  return '#' + combined.toString(16).slice(1).toUpperCase();
};

const folderBackColor = computed(() => darkenColor(props.color, 0.08));
const paper1Color = computed(() => darkenColor('#ffffff', 0.1));
const paper2Color = computed(() => darkenColor('#ffffff', 0.05));
const paper3Color = computed(() => '#ffffff');

const maxItems = 3;
const papers = computed(() => {
  const result = props.items.slice(0, maxItems);
  while (result.length < maxItems) {
    result.push(null);
  }
  return result;
});

const handleClick = () => {
  open.value = !open.value;
  if (!open.value) {
    paperOffsets.value = [
      { x: 0, y: 0 },
      { x: 0, y: 0 },
      { x: 0, y: 0 },
    ];
  }
  emit('click');
};

const handlePaperMouseMove = (e, index) => {
  if (!open.value) return;
  const rect = e.currentTarget.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const offsetX = (e.clientX - centerX) * 0.15;
  const offsetY = (e.clientY - centerY) * 0.15;
  paperOffsets.value[index] = { x: offsetX, y: offsetY };
};

const handlePaperMouseLeave = index => {
  paperOffsets.value[index] = { x: 0, y: 0 };
};
</script>

<template>
  <div
    class="animated-folder"
    :class="{ open: open }"
    :style="{ transform: `scale(${size})` }"
    @click="handleClick"
  >
    <div class="folder-container">
      <div
        class="folder-back"
        :style="{ '--folder-back-color': folderBackColor }"
      />

      <!-- Papers (documents) -->
      <div
        v-for="(item, i) in papers"
        :key="i"
        class="paper"
        :class="`paper-${i + 1}`"
        :style="{
          '--paper-color':
            i === 0 ? paper1Color : i === 1 ? paper2Color : paper3Color,
          '--magnet-x': open ? `${paperOffsets[i]?.x || 0}px` : '0px',
          '--magnet-y': open ? `${paperOffsets[i]?.y || 0}px` : '0px',
        }"
        @mousemove="e => handlePaperMouseMove(e, i)"
        @mouseleave="() => handlePaperMouseLeave(i)"
      >
        <slot :name="`item-${i}`" :item="item">
          {{ item }}
        </slot>
      </div>

      <div class="folder-front" :style="{ '--folder-color': color }" />
    </div>
  </div>
</template>

<style scoped>
.animated-folder {
  display: inline-block;
  transition: all 0.2s ease-in;
  cursor: pointer;
  user-select: none;
}

.animated-folder:not(.open):hover {
  transform: translateY(-8px) scale(var(--scale, 1));
}

.animated-folder:not(.open):hover .paper {
  transform: translate(-50%, 0%);
}

.animated-folder:not(.open):hover .folder-front {
  transform: skew(15deg) scaleY(0.6);
}

.animated-folder.open {
  transform: translateY(-8px) scale(var(--scale, 1));
}

.animated-folder.open .paper:nth-child(2) {
  transform: translate(-120%, -70%) rotateZ(-15deg);
  transition: all 0.3s ease-in-out;
}

.animated-folder.open .paper:nth-child(2):hover {
  transform: translate(
      calc(-120% + var(--magnet-x, 0px)),
      calc(-70% + var(--magnet-y, 0px))
    )
    rotateZ(-15deg) scale(1.1);
}

.animated-folder.open .paper:nth-child(3) {
  transform: translate(10%, -70%) rotateZ(15deg);
  height: 80%;
  transition: all 0.3s ease-in-out;
}

.animated-folder.open .paper:nth-child(3):hover {
  transform: translate(
      calc(10% + var(--magnet-x, 0px)),
      calc(-70% + var(--magnet-y, 0px))
    )
    rotateZ(15deg) scale(1.1);
}

.animated-folder.open .paper:nth-child(4) {
  transform: translate(-50%, -100%) rotateZ(5deg);
  height: 80%;
  transition: all 0.3s ease-in-out;
}

.animated-folder.open .paper:nth-child(4):hover {
  transform: translate(
      calc(-50% + var(--magnet-x, 0px)),
      calc(-100% + var(--magnet-y, 0px))
    )
    rotateZ(5deg) scale(1.1);
}

.animated-folder.open .folder-front {
  transform: skew(15deg) scaleY(0.6);
}

.folder-container {
  position: relative;
  width: 100px;
  height: 80px;
}

.folder-back {
  position: relative;
  width: 100px;
  height: 80px;
  background: var(--folder-back-color);
  border-radius: 0px 10px 10px 10px;
}

.folder-back::after {
  position: absolute;
  z-index: 0;
  bottom: 98%;
  left: 0;
  content: '';
  width: 30px;
  height: 10px;
  background: var(--folder-back-color);
  border-radius: 5px 5px 0 0;
}

.paper {
  position: absolute;
  z-index: 2;
  bottom: 10%;
  left: 50%;
  transform: translate(-50%, 10%);
  width: 70%;
  height: 80%;
  background: var(--paper-color);
  border-radius: 10px;
  transition: all 0.3s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #666;
}

.paper:nth-child(3) {
  width: 80%;
  height: 70%;
}

.paper:nth-child(4) {
  width: 90%;
  height: 60%;
}

.folder-front {
  position: absolute;
  z-index: 3;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--folder-color);
  border-radius: 5px 10px 10px 10px;
  transform-origin: bottom;
  transition: all 0.3s ease-in-out;
}
</style>
